import React from "react"
import { QueryStatus } from "@reduxjs/toolkit/query"
import { useGetCalendarSettingsQuery } from "../calendar-settings/api/index"
import { CalendarSettingsViewModel, CalendarSettingsViewModelById } from "../calendar-settings/api/calendar-settings"
import {
  CalendarSettingsIdInput,
  CalendarSettingsType,
  makeCalendarSettingsId,
} from "../calendar-settings/api/calendar-settings-id"
import { AppMainContextType } from "../types/app-context"
import { MONDAY } from "../constants"
import { useTranslation } from "react-i18next"
import { TimeZoneType } from "../constants/timezones"
import { makeDateFnsFormatToken } from "../utils/date-format"
import { makeCalendarSettingsKey } from "../calendar-settings/utils/settings-key"

export type UseCalendarSettingsInput = {
  mainContext: AppMainContextType | null
  calendarSettingsIds?: CalendarSettingsIdInput[]
  skipCalendarSettingsIds?: boolean
  skipAll?: boolean
}

const emptyCalendarSettingsById: CalendarSettingsViewModelById = {
  calendarSettingsById: {},
}

export const useCalendarSettings = ({
  mainContext,
  calendarSettingsIds = [],
  skipCalendarSettingsIds = false,
  skipAll = false,
}: UseCalendarSettingsInput): UseCalendarSettingsResult => {
  const translations = useTranslations()
  const queryCalendarSettingsIds = React.useMemo(
    () =>
      makeCalendarSettingsQueryIds({
        mainContext,
        calendarSettingsIds,
        skipCalendarSettingsIds,
        skipAll,
      }),
    [mainContext, calendarSettingsIds, skipCalendarSettingsIds, skipAll]
  )

  const {
    data: calendarSettingsById = emptyCalendarSettingsById,
    isLoading,
    isError,
    status,
  } = useGetCalendarSettingsQuery(queryCalendarSettingsIds, {
    refetchOnMountOrArgChange: true,
    skip: skipAll || queryCalendarSettingsIds.length === 0,
  })

  const calendarSettings = React.useMemo(
    () =>
      resolveMainContextCalendarSettings({
        mainContext,
        calendarSettingsById: calendarSettingsById.calendarSettingsById,
      }),
    [mainContext, calendarSettingsById.calendarSettingsById]
  )
  const settingsKey = React.useMemo(() => makeCalendarSettingsKey(calendarSettings), [calendarSettings])
  const queryErrors = React.useMemo(() => {
    const errors: CalendarSettingsQueryErrors = {}
    if (isError) errors.calendarSettings = translations.fetchErrorCalendarSettings
    return errors
  }, [isError, translations.fetchErrorCalendarSettings])

  return {
    calendarSettingsById: calendarSettingsById.calendarSettingsById,
    calendarSettingsIds: queryCalendarSettingsIds,
    calendarSettings,
    userCalendarSettings: resolveUserCalendarSettings(calendarSettingsById),
    firstDayOfWeek: calendarSettings.firstDayOfWeek,
    weekendDays: calendarSettings.weekendDays,
    settingsKey,
    queryErrors,
    isLoading,
    isError,
    status,
  }
}

export const makeCalendarSettingsQueryIds = ({
  mainContext,
  calendarSettingsIds = [],
  skipCalendarSettingsIds = false,
  skipAll = false,
}: UseCalendarSettingsInput): CalendarSettingsIdInput[] => {
  if (skipAll) return []

  const queryIds: CalendarSettingsIdInput[] = []
  const seenIds = new Set<string>()
  const addId = (id: CalendarSettingsIdInput) => {
    const calendarSettingsId = makeCalendarSettingsId(id)
    if (seenIds.has(calendarSettingsId)) return
    seenIds.add(calendarSettingsId)
    queryIds.push(id)
  }

  if (mainContext) addId({ type: mainContext.type, id: mainContext.id })
  if (!skipCalendarSettingsIds) calendarSettingsIds.forEach(addId)

  return queryIds
}

export const makeDefaultCalendarSettings = (
  id = "default",
  type: CalendarSettingsType = "user"
): CalendarSettingsViewModel => ({
  id,
  type,
  firstDayOfWeek: MONDAY,
  weekendDays: [],
  fixedHolidays: [],
  recurringHolidays: [],
  holidayDetails: [],
  dateFormatSpecs: {
    dateOrder: "MM.DD.YYYY",
    dateSeparator: ".",
  },
  timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone as TimeZoneType,
  dateFnsDateFormat: makeDateFnsFormatToken("MM.DD.YYYY"),
})

export const resolveMainContextCalendarSettings = ({
  mainContext,
  calendarSettingsById,
}: ResolveMainContextCalendarSettingsInput): CalendarSettingsViewModel => {
  const mainContextCalendarSettingsId = mainContext ? makeCalendarSettingsId(mainContext) : undefined
  const mainContextCalendarSettings = mainContextCalendarSettingsId
    ? calendarSettingsById[mainContextCalendarSettingsId]
    : undefined

  return mainContextCalendarSettings || makeDefaultCalendarSettings(mainContext?.id, mainContext?.type)
}

const resolveUserCalendarSettings = ({
  calendarSettingsById,
}: {
  calendarSettingsById: Record<string, CalendarSettingsViewModel>
}): CalendarSettingsViewModel => {
  const userCalendarSettingsId = Object.keys(calendarSettingsById).find((key) => key.startsWith("user:"))
  if (userCalendarSettingsId) return calendarSettingsById[userCalendarSettingsId]
  return makeDefaultCalendarSettings("user", "user")
}

const useTranslations = (defaults = defaultTranslations) => {
  const { t } = useTranslation("translation")

  return {
    fetchErrorCalendarSettings: t("failedToFetchDataError", defaults.fetchErrorCalendarSettings),
  }
}

const defaultTranslations = {
  fetchErrorCalendarSettings: "Failed to fetch calendar settings",
}

type CalendarSettingsQueryErrors = {
  calendarSettings?: string
}

type ResolveMainContextCalendarSettingsInput = {
  mainContext: AppMainContextType | null
  calendarSettingsById: Record<string, CalendarSettingsViewModel>
}

type UseCalendarSettingsResult = {
  calendarSettingsById: Record<string, CalendarSettingsViewModel>
  calendarSettingsIds: CalendarSettingsIdInput[]
  calendarSettings: CalendarSettingsViewModel
  userCalendarSettings: CalendarSettingsViewModel
  firstDayOfWeek: CalendarSettingsViewModel["firstDayOfWeek"]
  weekendDays: CalendarSettingsViewModel["weekendDays"]
  settingsKey: string
  queryErrors: CalendarSettingsQueryErrors
  isLoading: boolean
  isError: boolean
  status: QueryStatus
}
